import api from 'plugin/api';
import { API_URL } from 'types/const';
import format from 'utils/string/format';

/**
 * 리포트 목록 조회
 * @param {Object} params 요청 데이터
 * @param {number} params.reportType 구분 (1: 배합비 시행 계획, 2: 배합비 시행 계획 실적 비교, 3: 공장별 원료 가격 계획 비교, 4: 제품 원재료비 가격 비교, 5: 원재료비 예측, 6: IOMC 예측, 7: 생산성 분석, 8: 품질 결과 분석)
 * @param {string} [params.reportYm] 월
 * @param {string} params.buCode BU
 * @return {Object} 응답 데이터
 */
async function requestList(params) {
  return api.get(API_URL.sfms.report.list, { params });
}

/**
 * 배합비 시행 계획 리포트 생성
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {string} params.buCode BU
 * @return {Object} 응답 데이터
 */
async function requestInsertMixPlan(params) {
  return api.get(API_URL.sfms.report.mix.plan.insert, { params });
}

/**
 * 배합비 시행 계획 실적 비교 리포트 생성
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {number} params.factoryCodeSeq 공장
 * @return {Object} 응답 데이터
 */
async function requestInsertMixCompare(params) {
  return api.get(API_URL.sfms.report.mix.compare.insert, { params });
}

/**
 * 공장별 원료 가격 비교 리포트 생성
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @return {Object} 응답 데이터
 */
async function requestInsertPriceRawMaterial(params) {
  return api.get(API_URL.sfms.report.price.rawMaterial.insert, { params });
}

/**
 * 공장별 축종 제품 원재료비 비교 리포트 생성
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {string} params.buCode BU
 * @return {Object} 응답 데이터
 */
async function requestInsertPriceProduct(params) {
  return api.get(API_URL.sfms.report.price.product.insert, { params });
}

/**
 * 장기 원재료비 예측 리포트 생성
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @return {Object} 응답 데이터
 */
async function requestInsertPricePrediction(params) {
  return api.get(API_URL.sfms.report.price.prediction.insert, { params });
}

/**
 * 생산성 분석 리포트 생성
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {number} params.factoryCodeSeq 공장
 * @return {Object} 응답 데이터
 */
async function requestInsertAnalysisProduct(params) {
  return api.get(API_URL.sfms.report.analysis.product.insert, { params });
}

/**
 * 원료 품질 결과 분석 리포트 생성
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {number} params.factoryCodeSeq 공장
 * @return {Object} 응답 데이터
 */
async function requestInsertAnalysisQualityRawMaterial(params) {
  return api.get(API_URL.sfms.report.analysis.quality.rawMaterial.insert, {
    params
  });
}

/**
 * 제품 품질 결과 분석 리포트 생성
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {number} params.factoryCodeSeq 공장
 * @return {Object} 응답 데이터
 */
async function requestInsertAnalysisQualityProduct(params) {
  return api.get(API_URL.sfms.report.analysis.quality.product.insert, {
    params
  });
}

/**
 * 원료 품질 결과 분석 리포트 정보 조회
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {number} params.factoryCodeSeq 공장
 * @return {Object} 응답 데이터
 */
async function requestInformAnalysisQualityRawMaterial(params) {
  return api.get(API_URL.sfms.report.analysis.quality.rawMaterial.inform, {
    params
  });
}

/**
 * 제품 품질 결과 분석 리포트 정보 조회
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {number} params.factoryCodeSeq 공장
 * @return {Object} 응답 데이터
 */
async function requestInformAnalysisQualityProduct(params) {
  return api.get(API_URL.sfms.report.analysis.quality.product.inform, {
    params
  });
}

/**
 * 원료 품질 결과 분석 정보 조회
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {number} params.factoryCodeSeq 공장
 * @param {string} params.rwmtrCd 원료 코드
 * @param {number} params.searchType 분석방법 (0: 습식 분석, 1: NIR)
 * @return {Object} 응답 데이터
 */
async function requestAnalysisQualityRawMaterial(params) {
  return api.get(API_URL.sfms.report.analysis.quality.rawMaterial.analysis, {
    params
  });
}

/**
 * 제품 품질 결과 분석 정보 조회
 * @param {Object} params 요청 데이터
 * @param {string} params.reportYm 월
 * @param {number} params.factoryCodeSeq 공장
 * @param {string} params.prdtCd 제품 코드
 * @param {number} params.searchType 분석방법 (0: 습식 분석, 1: NIR)
 * @return {Object} 응답 데이터
 */
async function requestAnalysisQualityProduct(params) {
  return api.get(API_URL.sfms.report.analysis.quality.product.analysis, {
    params
  });
}

/**
 * 리포트 다운로드
 * @param {number} seq 리포트 번호
 * @return {Object} 응답 데이터
 */
async function requestDownload(seq) {
  return api.get(format(API_URL.sfms.report.download, { seq }), {
    responseType: 'arraybuffer'
  });
}

export {
  requestList,
  requestInsertMixPlan,
  requestInsertMixCompare,
  requestInsertPriceRawMaterial,
  requestInsertPriceProduct,
  requestInsertPricePrediction,
  requestInsertAnalysisProduct,
  requestInsertAnalysisQualityRawMaterial,
  requestInsertAnalysisQualityProduct,
  requestInformAnalysisQualityRawMaterial,
  requestInformAnalysisQualityProduct,
  requestAnalysisQualityRawMaterial,
  requestAnalysisQualityProduct,
  requestDownload
};
